import {
  DELAI_MUTATION_MS,
  gagnant,
  type BrouillonItem,
  type ChangementItem,
  type Item,
  type MsgServeur,
} from '@camellie/shared';
import { Socket } from './socket';
import { api } from '../api/client';

export interface Instantane {
  items: Item[];
  connecte: boolean;
  chargement: boolean;
  /** Message d'erreur transitoire a montrer une seconde ou deux. */
  incident: string | null;
}

interface EnAttente {
  id: string;
  /** Etat d'avant la mutation. null = creation optimiste. */
  avant: Item | null;
  minuterie: number;
  /**
   * Appele quand le serveur confirme une creation, avec l'item definitif.
   * Sert a l'assistant, qui doit connaitre les identifiants reels pour
   * pouvoir tout annuler d'un geste.
   */
  surCree?: (item: Item) => void;
}

let compteurRef = 0;
function nouvelleRef(): string {
  compteurRef += 1;
  return 'r' + String(Date.now()) + '-' + String(compteurRef);
}

/**
 * Etat local des items.
 *
 * Toute action de l'utilisateur est appliquee immediatement, puis confirmee ou
 * annulee par le serveur. Cocher une tache a 6 h du matin ne doit jamais
 * attendre un aller-retour reseau — mais ne doit jamais non plus mentir sur
 * ce qui a vraiment ete enregistre.
 */
class Magasin {
  private items = new Map<string, Item>();
  private connecte = false;
  private chargement = true;
  private incident: string | null = null;

  private readonly socket = new Socket();
  private readonly abonnes = new Set<() => void>();
  private readonly enAttente = new Map<string, EnAttente>();
  /** id d'item -> ref de la mutation en vol, pour ignorer les echos. */
  private readonly refParId = new Map<string, string>();

  private cache: Instantane = { items: [], connecte: false, chargement: true, incident: null };
  private cacheValide = false;

  private detacher: (() => void)[] = [];

  // ------------------------------------------------------------------ cycle

  demarrer(): void {
    if (this.detacher.length > 0) return;
    this.detacher.push(this.socket.surMessage((m) => this.recevoir(m)));
    this.detacher.push(
      this.socket.surEtat((c) => {
        this.connecte = c;
        this.invalider();
      }),
    );
    this.socket.connecter();
    void this.chargerInitial();
  }

  arreter(): void {
    for (const f of this.detacher) f();
    this.detacher = [];
    for (const p of this.enAttente.values()) clearTimeout(p.minuterie);
    this.enAttente.clear();
    this.refParId.clear();
    this.socket.fermer();
    this.items.clear();
    this.chargement = true;
    this.invalider();
  }

  /**
   * Premier remplissage par REST. Le socket enverra ensuite son propre sync,
   * mais l'API repond en general plus vite qu'une poignee de main WebSocket :
   * l'ecran se peint sans attendre.
   */
  private async chargerInitial(): Promise<void> {
    try {
      const { items } = await api.items();
      for (const item of items) this.fusionner(item);
      this.chargement = false;
      this.invalider();
    } catch {
      this.chargement = false;
      this.invalider();
    }
  }

  // ------------------------------------------------------------- abonnement

  abonner = (f: () => void): (() => void) => {
    this.abonnes.add(f);
    return () => this.abonnes.delete(f);
  };

  instantane = (): Instantane => {
    if (!this.cacheValide) {
      this.cache = {
        items: [...this.items.values()],
        connecte: this.connecte,
        chargement: this.chargement,
        incident: this.incident,
      };
      this.cacheValide = true;
    }
    return this.cache;
  };

  private invalider(): void {
    this.cacheValide = false;
    for (const f of this.abonnes) f();
  }

  private signaler(message: string): void {
    this.incident = message;
    this.invalider();
    window.setTimeout(() => {
      if (this.incident === message) {
        this.incident = null;
        this.invalider();
      }
    }, 4000);
  }

  // --------------------------------------------------------------- reception

  /**
   * Pose un item dans l'etat local, ou le retire s'il est annule.
   *
   * `annule` est la pierre tombale de la synchro : le serveur ne supprime
   * jamais une ligne, il la marque, ce qui permet au last-write-wins de
   * fonctionner et a un appareil revenu en ligne d'apprendre la suppression.
   * Cote client en revanche, un item annule doit disparaitre de la memoire,
   * sinon la carte grossit indefiniment.
   */
  private placer(item: Item): void {
    if (item.statut === 'annule') this.items.delete(item.id);
    else this.items.set(item.id, item);
  }

  private fusionner(item: Item): void {
    const actuel = this.items.get(item.id);
    if (actuel && gagnant(item, actuel) !== item) return;
    this.placer(item);
    this.socket.noterHorloge(item.maj_le);
  }

  private recevoir(msg: MsgServeur): void {
    switch (msg.t) {
      case 'sync': {
        if (msg.complet) {
          // Sync complet : on repart de l'etat serveur, en preservant les
          // mutations encore en vol pour ne pas faire clignoter l'ecran.
          const enVol = new Map<string, Item>();
          for (const id of this.refParId.keys()) {
            const i = this.items.get(id);
            if (i) enVol.set(id, i);
          }
          this.items.clear();
          for (const item of msg.items) this.fusionner(item);
          for (const [id, item] of enVol) this.items.set(id, item);
        } else {
          for (const item of msg.items) this.fusionner(item);
        }
        this.chargement = false;
        this.invalider();
        return;
      }

      case 'item': {
        // Echo d'une mutation encore en vol de notre cote : on laisse la
        // reponse `ok` trancher, sinon on afficherait brievement un etat
        // intermediaire.
        if (this.refParId.has(msg.item.id)) return;
        this.fusionner(msg.item);
        this.invalider();
        return;
      }

      case 'ok': {
        const p = this.enAttente.get(msg.ref);
        if (p) {
          this.resoudre(msg.ref, p);
          if (p.avant === null) p.surCree?.(msg.item);
        }
        // La version du serveur fait autorite, quelles que soient les horloges.
        if (p && p.avant === null) this.items.delete(p.id);
        this.placer(msg.item);
        this.socket.noterHorloge(msg.item.maj_le);
        this.invalider();
        return;
      }

      case 'item.retire': {
        // Menage cote serveur : ces lignes n'existent plus du tout. On ne
        // touche pas a une mutation encore en vol sur le meme id — la
        // reponse du serveur reste seule juge de son sort.
        let change = false;
        for (const id of msg.ids) {
          if (this.refParId.has(id)) continue;
          if (this.items.delete(id)) change = true;
        }
        if (change) this.invalider();
        return;
      }

      case 'rejet': {
        const p = this.enAttente.get(msg.ref);
        if (!p) return;
        this.resoudre(msg.ref, p);
        this.annuler(p, msg.item);
        this.signaler(msg.message);
        return;
      }

      default:
        return;
    }
  }

  private resoudre(ref: string, p: EnAttente): void {
    clearTimeout(p.minuterie);
    this.enAttente.delete(ref);
    if (this.refParId.get(p.id) === ref) this.refParId.delete(p.id);
  }

  /** Remet l'etat d'avant, ou celui que le serveur impose. */
  private annuler(p: EnAttente, autorite?: Item): void {
    if (autorite) {
      this.placer(autorite);
    } else if (p.avant) {
      this.items.set(p.id, p.avant);
    } else {
      this.items.delete(p.id);
    }
    this.invalider();
  }

  // ---------------------------------------------------------------- actions

  /**
   * Coche ou decoche. C'est le geste le plus frequent de l'app : il doit etre
   * instantane a l'ecran, et fiable au retour du serveur.
   */
  basculer(id: string): void {
    const actuel = this.items.get(id);
    if (!actuel) return;
    this.modifier(id, { statut: actuel.statut === 'fait' ? 'todo' : 'fait' });
  }

  /**
   * Supprime une tache.
   *
   * Cote serveur la ligne n'est pas effacee mais passee a `annule` : c'est ce
   * qui permet a l'autre telephone, s'il etait hors ligne, d'apprendre la
   * suppression a sa reconnexion. Une ligne reellement effacee serait
   * indistinguable d'une ligne jamais recue, et reapparaitrait.
   *
   * Cote client, la mise a jour optimiste la fait disparaitre tout de suite,
   * et un refus du serveur la fait revenir.
   */
  supprimer(id: string): void {
    this.modifier(id, { statut: 'annule' });
  }

  modifier(id: string, champs: ChangementItem): void {
    const avant = this.items.get(id);
    if (!avant) return;

    const ref = nouvelleRef();
    const optimiste: Item = {
      ...avant,
      ...champs,
      payload: (champs.payload ?? avant.payload) as Item['payload'],
      maj_le: new Date().toISOString(),
    };
    this.placer(optimiste);
    this.inscrire(ref, { id, avant });
    this.invalider();

    if (this.socket.envoyer({ t: 'item.maj', ref, id, champs })) return;

    // Socket ferme : on repasse par REST plutot que de bloquer l'utilisateur.
    void this.repli(ref, () => api.majItem(id, champs));
  }

  creer(brouillon: BrouillonItem, surCree?: (item: Item) => void): void {
    const ref = nouvelleRef();
    const maintenant = new Date().toISOString();
    const provisoire: Item = {
      id: 'tmp_' + ref,
      famille_id: '',
      type: brouillon.type,
      titre: brouillon.titre,
      statut: brouillon.statut ?? 'todo',
      assigne_a: brouillon.assigne_a ?? null,
      du_le: brouillon.du_le ?? null,
      recurrence: brouillon.recurrence ?? null,
      payload: (brouillon.payload ?? {}) as Item['payload'],
      cree_par: '',
      source: 'manuel',
      complete_par: null,
      complete_le: null,
      cree_le: maintenant,
      maj_le: maintenant,
    };
    this.items.set(provisoire.id, provisoire);
    this.inscrire(ref, { id: provisoire.id, avant: null, surCree });
    this.invalider();

    if (this.socket.envoyer({ t: 'item.creer', ref, brouillon })) return;
    void this.repli(ref, () => api.creerItem(brouillon));
  }

  private inscrire(ref: string, base: Omit<EnAttente, 'minuterie'>): void {
    const minuterie = window.setTimeout(() => {
      const p = this.enAttente.get(ref);
      if (!p) return;
      this.resoudre(ref, p);
      this.annuler(p);
      this.signaler('Pas de reponse du serveur. Modification annulee.');
    }, DELAI_MUTATION_MS);
    this.enAttente.set(ref, { ...base, minuterie });
    this.refParId.set(base.id, ref);
  }

  private async repli(ref: string, action: () => Promise<{ item: Item }>): Promise<void> {
    const p = this.enAttente.get(ref);
    if (!p) return;
    try {
      const { item } = await action();
      this.resoudre(ref, p);
      if (p.avant === null) {
        this.items.delete(p.id);
        p.surCree?.(item);
      }
      this.placer(item);
      this.socket.noterHorloge(item.maj_le);
      this.invalider();
    } catch (e) {
      this.resoudre(ref, p);
      this.annuler(p);
      this.signaler(e instanceof Error ? e.message : 'Echec de l enregistrement.');
    }
  }
}

export const magasin = new Magasin();
